import { createVuetify, type VuetifyOptions } from "vuetify";
import { VFileUpload } from "vuetify/labs/VFileUpload";
import "@/styles/main.scss";
import type { ThemeDefinition } from "vuetify";
import colors from "vuetify/util/colors";
import { sv } from "vuetify/locale";
import { md2 as blueprint } from "vuetify/blueprints";
import { getTheme } from "@/services/userSettings";

const oleterThemeLight: ThemeDefinition = {
  dark: false,
  colors: {
    background: colors.grey.lighten3,
    surface: colors.grey.lighten5,
    primary: "#006CB7",
    "primary-darken-1": "#004D98",
    secondary: colors.teal.darken1,
    "secondary-darken-1": colors.teal.darken2,
    // Tailwind red-700, the same as Nuxt UI's --ui-error (main.css): Vuetify's unlayered .bg-error/.text-error beat
    // Nuxt UI's, so both libraries need the one red
    error: "#c10007",
    info: colors.blue.base,
    success: colors.green.base,
    warning: colors.deepOrange.accent2,
    disabled: colors.grey.lighten1,
    unknown: colors.grey.base,
    registered: colors.purple.base,
    inProgress: "#006CB7",
    waitingForVerification: colors.orange.base,
    workDone: colors.green.base,
    readyForInvoicing: colors.indigo.base,
    invoiced: "#006CB7",
    finished: colors.teal.base,
    deleted: colors.grey.base,
    cancelled: colors.red.base,
  },
};
const oleterThemeDark: ThemeDefinition = {
  dark: true,
  colors: {
    background: colors.grey.darken4,
    surface: colors.grey.darken4,
    primary: "#006CB7",
    "primary-darken-1": "#004D98",
    secondary: colors.teal.lighten1,
    "secondary-darken-1": colors.teal.base,
    // Tailwind red-400, --ui-error in dark mode
    error: "#ff6467",
    info: colors.blue.lighten1,
    success: colors.green.lighten1,
    warning: colors.deepOrange.accent2,
    disabled: colors.grey.darken1,
    unknown: colors.grey.base,
    registered: colors.purple.lighten1,
    inProgress: colors.blue.lighten1,
    waitingForVerification: colors.orange.lighten1,
    workDone: colors.green.lighten1,
    readyForInvoicing: colors.indigo.lighten2,
    invoiced: colors.blue.lighten1,
    finished: colors.teal.lighten1,
    deleted: colors.grey.base,
    cancelled: colors.red.lighten1,
  },
};

const vuetifyOptions: VuetifyOptions = {
  components: { VFileUpload },
  locale: {
    locale: "sv",
    messages: { sv },
  },
  display: {
    mobileBreakpoint: "sm",
  },
  blueprint: blueprint,
  defaults: {
    VBtn: { variant: "text" },
    VDialog: {
      scrollStrategy: "none",
    },
    VAutocomplete: {
      clearable: true,
    },
  },
  theme: {
    // the saved choice, like VApp's :theme and html.dark (UseTheme): the global theme styles what renders outside
    // VApp (dialogs, menus), so a fixed "light" left those light in dark mode until the user switched once
    defaultTheme: getTheme(),
    themes: {
      light: oleterThemeLight,
      dark: oleterThemeDark,
    },
  },
};

export const createVuetifyPlugin = () => createVuetify(vuetifyOptions);

export type Theme = "dark" | "light";
